import chunk from 'lodash/chunk'
import React from 'react'
import { useAppState, useAppDispatch } from '../Context'

const DropdownField = ({ field, classNames, handleChange }) => {
  return (
    <div className="flex items-center">
      <span className="flex-shrink-0 w-5 text-center"></span>
      <select id={field.name} className={classNames} name={field.name} value={field.value} onChange={handleChange}>
        {field.options.map((option) => (
          <option key={option.name} value={option.value}>
            {option.name}
          </option>
        ))}
      </select>
      <span className="flex-shrink-0 w-5 text-center"></span>
    </div>
  )
}

const DateField = ({ field, classNames, inputMode, handleChange }) => {
  return (
    <div className="flex items-center">
      <span className="flex-shrink-0 w-5 text-center"></span>
      <input
        id={field.name}
        className={classNames}
        type="date"
        inputMode={inputMode}
        name={field.name}
        value={field.value}
        onChange={handleChange}
      />
      <span className="flex-shrink-0 w-5 text-center"></span>
    </div>
  )
}

const NumberField = ({ field, classNames, inputMode, handleChange }) => {
  return (
    <div className="flex items-center">
      <span className="flex-shrink-0 w-5 text-center">{field.displayAs && field.displayAs === 'currency' && '$'}</span>
      <input
        id={field.name}
        className={classNames}
        type={field.type}
        inputMode={inputMode}
        name={field.name}
        value={field.value}
        onChange={handleChange}
      />
      <span className="flex-shrink-0 w-5 text-center">{field.displayAs && field.displayAs === 'percent' && '%'}</span>
    </div>
  )
}

const HeaderField = ({ field }) => {
  return <div className="p-3 font-bold bg-gray-100">{field.text}</div>
}

export function InputFields() {
  const { fields } = useAppState()
  const dispatch = useAppDispatch()

  const handleChange = (e) => {
    dispatch({ type: 'setField', payload: { name: e.target.name, value: e.target.value } })
  }

  return (
    <form className="border-b border-gray-300 divide-y divide-gray-300">
      {fields.map((field) => {
        let classNames = `py-1 px-2 w-32 text-base text-right bg-white rounded-md border ${
          field.hasError ? 'border-error' : 'border-gray-300'
        }`

        const inputMode = field.type === 'number' ? 'decimal' : 'text'

        if (field.type === 'header') {
          return <HeaderField key={field.text} field={field} />
        }

        return (
          <div
            key={field.name}
            className={`flex justify-between items-center p-3 ${field.showHighlighted && 'bg-green bg-opacity-5'}`}
          >
            <label htmlFor={field.name}>
              <span dangerouslySetInnerHTML={{ __html: field.label }} />:
            </label>
            {field.type === 'date' && (
              <DateField field={field} classNames={classNames} inputMode={inputMode} handleChange={handleChange} />
            )}
            {field.type === 'dropdown' && (
              <DropdownField field={field} classNames={classNames} handleChange={handleChange} />
            )}
            {field.type === 'number' && (
              <NumberField field={field} classNames={classNames} inputMode={inputMode} handleChange={handleChange} />
            )}
          </div>
        )
      })}
    </form>
  )
}

export function LoanCompareFields() {
  const { fields } = useAppState()
  const dispatch = useAppDispatch()

  const handleChange = (e) => {
    dispatch({ type: 'setField', payload: { name: e.target.name, value: e.target.value } })
  }

  const nonDoubleFields = fields.filter((field) => field.singleRow)
  const doubleFields = fields.filter((field) => !field.singleRow)
  const compareFields = chunk(doubleFields, 2)

  return (
    <form className="border-b border-gray-300 divide-y divide-gray-300">
      {nonDoubleFields.map((field) => {
        let classNames = `py-1 px-2 w-32 text-base text-right bg-white rounded-md border ${
          field.hasError ? 'border-error' : 'border-gray-300'
        }`

        const inputMode = field.type === 'number' ? 'decimal' : 'text'

        if (field.type === 'header') {
          return <HeaderField key={field.text} field={field} />
        }

        return (
          <div
            key={field.name}
            className={`flex justify-between items-center p-3 ${field.showHighlighted && 'bg-green bg-opacity-5'}`}
          >
            <label htmlFor={field.name}>
              <span dangerouslySetInnerHTML={{ __html: field.label }} />:
            </label>
            {field.type === 'date' && (
              <DateField field={field} classNames={classNames} inputMode={inputMode} handleChange={handleChange} />
            )}
            {field.type === 'dropdown' && (
              <DropdownField field={field} classNames={classNames} handleChange={handleChange} />
            )}
            {field.type === 'number' && (
              <NumberField field={field} classNames={classNames} inputMode={inputMode} handleChange={handleChange} />
            )}
          </div>
        )
      })}

      <div className="flex justify-end items-center py-3 px-5 font-bold bg-gray-100">
        <span className="w-32 font-bold text-center md:w-40">Loan 1</span>
        <span className="w-32 font-bold text-center md:w-40">Loan 2</span>
      </div>

      {compareFields.map((group, index) => {
        return (
          <div key={index} className={`flex flex-wrap justify-end items-center p-3`}>
            {group.map((field, index) => {
              const inputMode = field.type === 'number' ? 'decimal' : 'text'

              let classNames = `py-1 px-2 w-24 md:w-32 text-base text-right bg-white rounded-md border ${
                field.hasError ? 'border-error' : 'border-gray-300'
              }`

              return (
                <React.Fragment key={field.name}>
                  <label
                    htmlFor={field.name}
                    className={`sm-down:w-full sm-down:mb-4 md:mr-auto ${index === group.length - 1 && 'sr-only'}`}
                  >
                    <span dangerouslySetInnerHTML={{ __html: field.label }} />:
                  </label>
                  {field.type === 'date' && (
                    <DateField
                      field={field}
                      classNames={classNames}
                      inputMode={inputMode}
                      handleChange={handleChange}
                    />
                  )}
                  {field.type === 'dropdown' && (
                    <DropdownField field={field} classNames={classNames} handleChange={handleChange} />
                  )}
                  {field.type === 'number' && (
                    <NumberField
                      field={field}
                      classNames={classNames}
                      inputMode={inputMode}
                      handleChange={handleChange}
                    />
                  )}
                </React.Fragment>
              )
            })}
          </div>
        )
      })}
    </form>
  )
}
